import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePortfolioStore, type Project } from "../stores/portfolioStore";
import { useLocation } from "@tanstack/react-router";
import LaserBorder from "./layout/LaserBorder";
import MagneticButton from "./motion/MagneticButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import {
  canUseCinematicMotion,
  gsap,
  scrollToElement,
} from "../lib/motion";

type CategoryFilter = "All" | "Web" | "Mobile" | "Product";

const FILTERS: CategoryFilter[] = ["All", "Web", "Mobile", "Product"];

const Projects: React.FC = () => {
  const {
    projects,
    loading,
    modalLoading,
    showModal,
    screenshots,
    currentSlide,
    showScreenshots,
    closeModal,
    setCurrentSlide,
  } = usePortfolioStore();

  const location = useLocation();
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [filter, setFilter] = useState<CategoryFilter>("All");
  const sectionRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useScrollReveal(sectionRef);

  const splitString = (str: string): string[] => {
    return str.split(",").map((tech) => tech.trim());
  };

  const openPrimaryAction = (project: Project) => {
    if (project.video_path) {
      setShowVideoPreview(true);
    } else if (project.link) {
      window.open(project.link, "_blank", "noopener,noreferrer");
    } else {
      showScreenshots(project.id, project.type || 1);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "All") return projects;
    return projects.filter((p) => p.category === filter);
  }, [projects, filter]);

  useEffect(() => {
    if (location.hash === "#projects") {
      window.setTimeout(() => scrollToElement("#projects"), 100);
    }
  }, [location.hash]);

  // Stagger project strips when filter/list changes
  useEffect(() => {
    if (!listRef.current || !canUseCinematicMotion()) return;
    const items = listRef.current.querySelectorAll("[data-project-item]");
    if (!items.length) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        items,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.08,
          ease: "power2.out",
          clearProps: "transform",
        }
      );
    }, listRef);

    return () => ctx.revert();
  }, [filter, filtered.length, loading]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showModal) return;

      switch (e.key) {
        case "Escape":
          closeModal();
          break;
        case "ArrowLeft":
          if (currentSlide > 0) setCurrentSlide(currentSlide - 1);
          break;
        case "ArrowRight":
          if (currentSlide < screenshots.length - 1) {
            setCurrentSlide(currentSlide + 1);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    showModal,
    currentSlide,
    screenshots.length,
    closeModal,
    setCurrentSlide,
  ]);

  if (loading) {
    return (
      <div className="loading_body">
        <div className="matrix-bg"></div>
        <div className="loading-bubbles">
          <div className="loading-bubble"></div>
          <div className="loading-bubble"></div>
          <div className="loading-bubble"></div>
        </div>
        <div>Compiling awesome projects...</div>
      </div>
    );
  }

  return (
    <div id="projects" ref={sectionRef} className="projects-section row">
      <div className="container mt-10">
        <div className="row">
          <div className="col-12" data-reveal>
            <h1 className="page-title text-center mb-3">
              <span className="code-style md:text-5xl text-3xl">
                &#60;Featured Works/&#62;
              </span>
            </h1>
            <p className="text-center text-white/65 mb-6 max-w-2xl mx-auto">
              Selected client and product work — filter by type.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div
          data-reveal
          className="flex flex-wrap justify-center gap-2 mb-10"
          role="tablist"
          aria-label="Project categories"
        >
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filter === f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-300 border ${
                filter === f
                  ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-300"
                  : "bg-white/5 border-white/10 text-white/70 hover:border-white/25 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div ref={listRef}>
          {filtered.map((project, index) => (
            <div
              key={project.id}
              data-project-item
              className="project-item mb-5"
            >
              <div className="row align-items-center">
                <div
                  className={`col-lg-6 ${
                    index % 2 === 1 ? "order-lg-2" : ""
                  } flex items-center h-full`}
                >
                  <div
                    className="project-image-container max-h-[400px] flex items-center justify-center md:py-10 md:pt-0 pt-4 p mx-4 rounded-2xl md:my-0 my-4 h-full w-full cursor-pointer"
                    onClick={() => openPrimaryAction(project)}
                  >
                    <img
                      src={
                        new URL(
                          `../assets/uploads/${project.banner}`,
                          import.meta.url
                        ).href
                      }
                      alt={project.title}
                      className="project-image rounded-lg max-w-full h-auto block mx-auto"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          "https://via.placeholder.com/600x400/374151/FFFFFF?text=Project+Image";
                      }}
                    />
                    {!project.video_path && (
                      <div className="project-image-overlay">
                        <LaserBorder
                          bg="linear-gradient(135deg, #1ab79b 0%, #009e9e 100%)"
                          className="py-3"
                        >
                          <span className="text-white relative z-1">
                            {project.link ? "Visit live site" : "View screenshots"}
                          </span>
                        </LaserBorder>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={`col-lg-6 ${index % 2 === 1 ? "order-lg-1" : ""}`}
                >
                  <div className="project-content">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {project.category && (
                        <span className="text-xs font-mono uppercase tracking-wider text-cyan-400/90 px-2 py-0.5 rounded border border-cyan-400/30">
                          {project.category}
                        </span>
                      )}
                      <span className="text-xs font-mono uppercase tracking-wider text-cyan-400/90">
                        {project.role || "Full-stack"}
                      </span>
                      <span className="user-count text-sm text-white/50">
                        Users: {project.user_count}
                      </span>
                    </div>
                    <h3 className="project-title">{project.title}</h3>

                    {project.outcome && (
                      <p className="text-cyan-200/80 font-medium mb-2">
                        {project.outcome}
                      </p>
                    )}

                    <p className="project-description mb-4">
                      {project.description}
                    </p>

                    <div className="project-technologies mb-4">
                      {splitString(project.technology).map(
                        (tech, techIndex) => (
                          <span
                            key={techIndex}
                            className="tech-badge bg-white/10 text-gray-100"
                          >
                            {tech}
                          </span>
                        )
                      )}
                    </div>

                    <div className="project-actions flex flex-wrap gap-2">
                      {project.video_path ? (
                        <MagneticButton
                          className="btn btn-primary !inline-flex"
                          onClick={() => setShowVideoPreview(true)}
                        >
                          Preview
                        </MagneticButton>
                      ) : project.link ? (
                        <MagneticButton
                          as="a"
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary !inline-flex"
                        >
                          Visit live site
                        </MagneticButton>
                      ) : (
                        <MagneticButton
                          className="btn btn-primary !inline-flex"
                          onClick={() =>
                            showScreenshots(project.id, project.type || 1)
                          }
                        >
                          View screenshots
                        </MagneticButton>
                      )}
                      {project.video_path && project.link && (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-light"
                        >
                          Live site
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-white/50 py-12">
            No projects in this category yet.
          </p>
        )}
      </div>

      {showVideoPreview && (
        <div
          className="modal-overlay"
          onClick={() => setShowVideoPreview(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex items-center">
                <h5 className="modal-title">Tarif Portal Preview</h5>
                <button
                  type="button"
                  className="close md:hidden"
                  onClick={() => setShowVideoPreview(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <button
                type="button"
                className="close md:!flex !hidden"
                onClick={() => setShowVideoPreview(false)}
              >
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div
                className="video-container"
                style={{ width: "100%", maxWidth: "100%" }}
              >
                <video
                  controls
                  autoPlay
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "8px",
                  }}
                >
                  <source
                    src={
                      new URL("../assets/vids/pricify.mp4", import.meta.url)
                        .href
                    }
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex items-center">
                <h5 className="modal-title">Project Screenshots</h5>
                <button
                  type="button"
                  className="close md:hidden"
                  onClick={closeModal}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-controls md:!pt-0 pt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm me-2"
                  onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                  disabled={currentSlide === 0}
                >
                  <i className="fas fa-chevron-left"></i> Previous
                </button>
                <span className="carousel-counter">
                  {currentSlide + 1} / {screenshots.length}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm ms-2"
                  onClick={() =>
                    setCurrentSlide(
                      Math.min(screenshots.length - 1, currentSlide + 1)
                    )
                  }
                  disabled={currentSlide === screenshots.length - 1}
                >
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              <button
                type="button"
                className="close md:!flex !hidden"
                onClick={closeModal}
              >
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {modalLoading ? (
                <div className="loading_body loading_modal">
                  <div className="matrix-bg"></div>
                  <div className="loading-bubbles">
                    <div className="loading-bubble"></div>
                    <div className="loading-bubble"></div>
                    <div className="loading-bubble"></div>
                  </div>
                  <div>Rendering screenshots...</div>
                </div>
              ) : (
                <div className="carousel-container">
                  {screenshots.length > 0 && (
                    <div className="carousel">
                      <div className="image-container">
                        <img
                          src={
                            new URL(
                              `../assets/uploads/${screenshots[currentSlide].path}`,
                              import.meta.url
                            ).href
                          }
                          alt={`Screenshot ${currentSlide + 1}`}
                          className="carousel-image rounded rounded-lg"
                          id="carousel-image"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src =
                              "https://via.placeholder.com/800x600/374151/FFFFFF?text=Screenshot+Not+Available";
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
